import React, { useState, useEffect, useRef } from "react";
import { Bell } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";

import { useNavigate } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import {
  selectNotifications,
  selectUnreadCount,
} from "../../store/notification-slice";
import {
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "../../store/apis/notification-thunk";

const NotificationIcon = () => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const unreadCount = useSelector(selectUnreadCount);
  const notifications = useSelector(selectNotifications);
  const dropdownRef = useRef(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle notification click
  const handleNotificationClick = (notification) => {
    dispatch(markNotificationAsRead(notification.id));

    // Navigate based on notification type if needed
    if (notification.link) {
      navigate(notification.link);
    }

    setDropdownOpen(false);
  };

  // Mark all as read
  const handleMarkAllAsRead = (e) => {
    e.stopPropagation();
    dispatch(markAllNotificationsAsRead());
  };

  // View all notifications
  const handleViewAll = () => {
    navigate("/ntf");
    setDropdownOpen(false);
  };

  return (
    <div className="relative profile-dropdown" ref={dropdownRef}>
      <button
        onClick={() => setDropdownOpen(!dropdownOpen)}
        className={`w-fit shadow-inputshadow rounded-sm md:rounded-5px overflow-hidden inline-flex items-center justify-center py-[12px] px-2 text-[8px] sm:text-xs leading-none font-medium transition-all duration-300 bg-buttonGradient bg-no-repeat text-white hover:text-white hover:bg-buttonGradientHover relative`}
      >
        <Bell
          className={`text-darkblue dark:text-white size-4 sm:size-5 s18:size-[26px]`}
        />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 bg-yellow text-darkGreen text-[8px] rounded-full w-4 h-4 flex items-center justify-center">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {dropdownOpen && (
        <div className="absolute right-0 mt-2 w-80 rounded-lg overflow-hidden shadow-lg z-50 bg-buttonGradient bg-no-repeat text-white">
          <div className="py-2">
            {/* Header */}
            <div className="px-4 py-3 bg-[#4F5F75] flex justify-between items-center">
              <span className="text-sm font-semibold text-white">
                Notifications
              </span>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  className="text-xs text-yellow hover:text-yellow-dark transition-colors duration-150"
                >
                  Mark all as read
                </button>
              )}
            </div>

            {/* Notification list */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length > 0 ? (
                notifications.slice(0, 5).map((notification) => (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full flex items-start px-4 py-3 text-left hover:bg-[#4F5F75] transition-colors duration-150 border-b border-gray-700 ${
                      !notification.read ? "bg-[#3D4B5C]/40" : ""
                    }`}
                  >
                    {/* Notification icon */}
                    <div
                      className={`flex-shrink-0 rounded-full p-2 mr-3 ${
                        getNotificationTypeStyle(notification.type).bgColor
                      }`}
                    >
                      {getNotificationTypeStyle(notification.type).icon}
                    </div>

                    {/* Notification content */}
                    <div className="flex-1">
                      <p className="text-sm text-white mb-1">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400">
                        {formatDistanceToNow(new Date(notification.createdAt), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>

                    {/* Unread indicator */}
                    {!notification.read && (
                      <div className="flex-shrink-0 h-2 w-2 mt-2 rounded-full bg-yellow"></div>
                    )}
                  </button>
                ))
              ) : (
                <div className="px-4 py-6 text-center text-gray-400">
                  <p>No notifications yet</p>
                </div>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="px-4 py-3 bg-[#4F5F75] text-center">
                <button
                  onClick={handleViewAll}
                  className="text-sm text-yellow hover:text-yellow-dark transition-colors duration-150"
                >
                  View all notifications
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Helper function to get notification type styles
const getNotificationTypeStyle = (type) => {
  switch (type) {
    case "deposit":
      return {
        bgColor: "bg-green-500/20",
        icon: <Bell size={16} className="text-green-500" />,
      };
    case "withdrawal":
      return {
        bgColor: "bg-red-500/20",
        icon: <Bell size={16} className="text-red-500" />,
      };
    case "bet":
      return {
        bgColor: "bg-blue-500/20",
        icon: <Bell size={16} className="text-blue-500" />,
      };
    case "win":
      return {
        bgColor: "bg-yellow-500/20",
        icon: <Bell size={16} className="text-yellow-500" />,
      };
    default:
      return {
        bgColor: "bg-gray-500/20",
        icon: <Bell size={16} className="text-gray-500" />,
      };
  }
};

export default NotificationIcon;
